/*
 * Gallery behaviour for _vp-pdp-gallery-hover.
 *
 * Slides are stacked and toggled by class — no transform carousel — so there is no scroll
 * position to get out of sync and nothing to mis-measure. Arrows wrap. Thumbnails act as a
 * tablist with roving tabindex and arrow-key support.
 *
 * Hover preview is mouse-only and delayed, so sweeping the cursor across the strip does not
 * thrash the main image, and touch keeps plain tap-to-select.
 *
 * Nothing about the children is cached. Switching version morphs this section in place, which
 * keeps THIS element (so connectedCallback never runs again) while replacing every slide and
 * thumbnail beneath it. A stored array would then describe the previous product — 20 slides
 * where the DOM now has 17 — and selecting a colour would toggle classes on nodes nobody can
 * see. So the lists are read live and the listeners are delegated to the host.
 */

const HOVER_DELAY = 110;

class VpPdpGallery extends HTMLElement {
  #timer;
  #controller;

  /** Read live: a morph can replace every child without this element being reconnected. */
  get track() { return this.querySelector('.vp-gal__track'); }
  get slides() { return [...this.querySelectorAll('.vp-gal__slide')]; }
  get thumbs() { return [...this.querySelectorAll('.vp-gal__thumb')]; }

  connectedCallback() {
    this.index = 0;
    const { signal } = (this.#controller = new AbortController());

    // Delegated to the host so a swapped-in set of thumbnails is wired with no re-binding.
    this.addEventListener('click', (event) => {
      // The badge on a video slide invites a click, so honour it: toggle that video.
      const videoSlide = event.target.closest?.('.vp-gal__slide--video');
      if (videoSlide) {
        const video = videoSlide.querySelector('.vp-gal__video');
        if (video && !video.paused) {
          video.pause();
          videoSlide.classList.remove('is-playing');
        } else {
          this.#playVideo(videoSlide);
        }
        return;
      }

      const thumb = event.target.closest?.('.vp-gal__thumb');
      if (thumb) return this.show(this.thumbs.indexOf(thumb));

      const arrow = event.target.closest?.('.vp-gal__arrow');
      if (!arrow) return;
      const count = this.slides.length;
      if (!count) return;
      const dir = Number(arrow.dataset.dir) || 1;
      this.show((this.index + dir + count) % count, true);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      const thumb = event.target.closest?.('.vp-gal__thumb');
      if (thumb) this.#onKey(event, this.thumbs.indexOf(thumb));
    }, { signal });

    if (this.dataset.hover === 'true') {
      this.addEventListener('pointerover', (event) => {
        if (event.pointerType !== 'mouse') return;
        const thumb = event.target.closest?.('.vp-gal__thumb');
        if (!thumb) return;
        const i = this.thumbs.indexOf(thumb);
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.show(i), HOVER_DELAY);
      }, { signal });
      this.addEventListener('pointerout', () => clearTimeout(this.#timer), { signal });
    }

    this.#warmVariantImages();

    // Picking a seat or leg colour re-renders the section server-side and announces it with
    // `variant:update`, carrying the new markup. Without this the gallery keeps whatever media
    // it was first rendered with, so the chair never changes colour. Same swap the theme's own
    // media-gallery.js performs; the event name is defined in assets/events.js.
    this.closest('.shopify-section, dialog')?.addEventListener(
      'variant:update',
      this.#onVariantUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
  }

  /**
   * Warm the images a colour swap can land on, once the browser is idle.
   *
   * Flipping `loading` on the real <img> rather than fetching a URL by hand is deliberate: the
   * browser then resolves the element's own srcset/sizes, so it warms exactly the candidate the
   * gallery will display (600w here, not the 2160w original) and the swap is a cache hit. Runs
   * at idle with a low priority so it never competes with the first paint. Opt-in.
   */
  /** Plays the video on the open slide, pauses every other. */
  #syncVideo(index = this.index) {
    const slides = this.slides;
    for (const [n, slide] of slides.entries()) {
      const video = slide.querySelector('.vp-gal__video');
      if (!video) continue;
      if (n === index && this.dataset.videoPlayOnShow === 'true') {
        this.#playVideo(slide);
      } else {
        video.pause();
        slide.classList.remove('is-playing');
      }
    }
  }

  #playVideo(slide) {
    const video = slide.querySelector('.vp-gal__video');
    if (!video) return;
    // preload is none until something asks, and no source is chosen until a load happens —
    // raising preload on its own does not start one.
    if (video.preload === 'none') {
      video.preload = 'auto';
      video.load();
    }
    const started = video.play();
    if (started?.then) started.then(() => slide.classList.add('is-playing')).catch(() => {});
    else slide.classList.add('is-playing');
  }

  #warmVariantImages() {
    const ids = this.dataset.preloadMedia;
    if (!ids) return;

    const warm = () => {
      for (const slide of this.slides) {
        const id = slide.dataset.mediaId;
        if (!id || !ids.includes(`,${id},`)) continue;
        const img = slide.querySelector('img');
        if (!img || img.complete) continue;
        img.fetchPriority = 'low';
        img.loading = 'eager';
      }
    };

    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
    else setTimeout(warm, 1200);
  }

  /**
   * Show the image belonging to the newly selected variant.
   *
   * Deliberately NOT the swap the theme's media-gallery.js performs. That one replaces itself
   * from `detail.data.html`, but for a PDP the variant picker fetches the product URL without
   * the `?view=` parameter, so the response is the DEFAULT template — which renders a different
   * gallery entirely and contains no <vp-pdp-gallery> to swap in. Every slide is already in the
   * DOM, so the variant's `featured_media` id is all that is needed and there is no round trip.
   */
  #onVariantUpdate = (event) => {
    const mediaId = event.detail?.resource?.featured_media?.id;
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.show(index);
  };

  /** @param {KeyboardEvent} event @param {number} i */
  #onKey(event, i) {
    const last = this.thumbs.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.show(next);
    this.thumbs[next].focus();
  }

  /** @param {number} i @param {boolean} [animate] */
  show(i, animate = false) {
    const slides = this.slides;
    if (i < 0 || i >= slides.length) return;
    if (i === this.index) return;
    this.index = i;

    // The class is toggled per call rather than left on, so a hover immediately after an arrow
    // click is still instant instead of inheriting the arrow's transition.
    this.track?.classList.toggle('is-sliding', animate);
    this.track?.style.setProperty('--vp-gal-i', String(i));
    this.#syncVideo(i);
    for (const [n, slide] of slides.entries()) {
      const on = n === i;
      slide.classList.toggle('is-active', on);
      slide.toggleAttribute('aria-hidden', !on);
    }
    for (const [n, thumb] of this.thumbs.entries()) {
      const on = n === i;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-selected', String(on));
      thumb.tabIndex = on ? 0 : -1;
    }
  }
}

if (!customElements.get('vp-pdp-gallery')) {
  customElements.define('vp-pdp-gallery', VpPdpGallery);
}
